import { yupResolver } from '@hookform/resolvers/yup';
import { SettingsOutlined, Cancel, NavigateNext, Close } from '@mui/icons-material';
import { Modal, Box, InputLabel, Stack } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { editTeam, createTeam } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  IUserDTO,
} from 'wisestep-base';

import { useUserContext, useWorkspaceContext } from '../../../context';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { defaultTeamName } from '../helper/defaultValues';
import { createTeamPayload } from '../helper/payload';
import { teamSchema } from '../helper/validation';

export interface ICreateTeamModal {
  teamId?: string;
  open: boolean;
  onEditSuccess: () => void;
  onCreateSuccess: (teamName?: string) => void;
  handleClose: () => void;
}

const CreateTeamModal = ({
  teamId,
  open,
  onEditSuccess,
  onCreateSuccess,
  handleClose,
}: ICreateTeamModal) => {
  const user = useUserContext().userDTO as IUserDTO;
  const workspaceContext = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    resolver: yupResolver(teamSchema),
    defaultValues: defaultTeamName,
  });

  interface ITeamFormData {
    teamName: string;
  }

  const handleTeamCreation = async (data: ITeamFormData) => {
    const payload = {
      ...createTeamPayload,
      id: teamId || null,
      name: data.teamName,
      ownerEmail: user.email,
      ownerId: user.id,
      type: 'provider',
      workspaceId: workspaceContext?.id,
    };
    if (teamId) {
      await editTeam(payload);
      onEditSuccess();
    } else {
      const result = await createTeam(payload);
      if (result?.data?.name !== null) onCreateSuccess(data.teamName);
      else {
        errorNotification();
      }
    }
    handleClose();
  };
  const errorNotification = () =>
    setNotification(failed(`Team Name already exists in the Workspace`));

  return (
    <Modal open={open} onClose={handleClose}>
      <Box bgcolor={WsColors.White} m={28} borderRadius={1.5}>
        <FormProvider {...methods}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            p={2}
            bgcolor={WsColors.TertiaryShades50}
            borderRadius={'inherit'}
          >
            <WsTypography variant={WsTypoVariants.BODY1}>
              {teamId ? 'Edit Team Name' : 'Create Team'}
            </WsTypography>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <SettingsOutlined sx={{ fill: WsColors.TheBlues500 }} />
              <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.PRIMARY}>
                Configure Alert Settings
              </WsTypography>
              <Close
                sx={{ fill: WsColors.TertiaryShades400, cursor: 'pointer' }}
                onClick={handleClose}
              />
            </Stack>
          </Stack>
          <Box p={2} component={'form'} onSubmit={methods.handleSubmit(handleTeamCreation)}>
            <InputLabel>Team Name</InputLabel>
            <ControlledTextInput
              name="teamName"
              fullWidth
              type="text"
              placeholder="ex: Team Slytherin"
            />
            <Stack direction={'row'} spacing={2} justifyContent={'flex-end'} mt={2}>
              <WsButton variant={'outlined'} endIcon={<Cancel />} onClick={handleClose}>
                Cancel
              </WsButton>
              <WsButton variant={'contained'} endIcon={<NavigateNext />} type="submit">
                {teamId ? 'Confirm' : 'Create Team'}
              </WsButton>
            </Stack>
          </Box>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export default CreateTeamModal;
